import { ObjectType } from '../Types/base';
import { GenericTranslation } from '../Types/culture';
import {
	MbfObject,
	LinkedMediabankFile,
	MediabankFile,
	MediabankFileSettings,
	MediabankSettings,
	MediabankUploadLocation,
} from '../Types/mediabank';
import { get, getPathWithRootPath, post, upload } from './common';

export type MediabankGetFilesParams = {
	FileTypes?: number[];
	TagIds?: number[];
	GetRelatedFiles?: boolean;
	IncludeViews?: boolean;
	UseComments?: boolean;
	MapLikes?: boolean;
	LoadCommonLocals?: boolean;
	GetDummyContentIfDbEmpty?: boolean;
	JourneyOrder?: boolean;
	ObjectTypes?: ObjectType[];
	ObjectIds?: number[];
	FillSourceUrls?: boolean;
};

const apiPath = 'api/mediabankuser/';

export async function getFiles(params: MediabankGetFilesParams) {
	const path = `${apiPath}GetMediaBankFilesForUser`;
	const files = await post<MediabankFile[]>(path, params);
	return files;
}

export async function unlinkFileFromObject(fileId: number, objectType: ObjectType, objectId: number) {
	const path = `${apiPath}UnlinkObjectFromMediabankFile?mediabankFileId=${fileId}&objectType=${objectType}&objectId=${objectId}`;
	await post(path);
}

export async function updateNameAndDescription(fileId: number, name: string, description: string) {
	const path = `${apiPath}UpdateNameAndDescriptionForFile?mbfId=${fileId}&name=${name}&desc=${description}`;
	await post(path);
}

export function formatUploadFileFields(
	name: string,
	description: string,
	isPrivate: boolean,
	isInvisible: boolean,
	uploadedFrom: MediabankUploadLocation,
	settings?: MediabankSettings,
	nameTranslations?: GenericTranslation[],
	descriptionTranslations?: GenericTranslation[],
	urlRewriteTranslations?: GenericTranslation[]
) {
	const fields: Record<string, string> = {
		name: name,
		description: description,
		uploadedFrom: uploadedFrom.toString(),
		isPrivate: isPrivate?.toString() ?? 'false',
		isInvisible: isInvisible?.toString() ?? 'false',
		NameTranslations: nameTranslations ? JSON.stringify(nameTranslations) : '',
		DescriptionTranslations: descriptionTranslations ? JSON.stringify(descriptionTranslations) : '',
		urlRewriteTranslations: urlRewriteTranslations ? JSON.stringify(urlRewriteTranslations) : '',
		skipDefaultPermissions: settings?.skipDefaultPermissions?.toString() ?? 'false',
		skipResize: settings?.skipResize?.toString() ?? 'false',
		linkToObjectType: settings?.linkToObject?.ObjectType.toString() ?? '',
		linkToObjectId: settings?.linkToObject?.ObjectId?.toString() ?? '',
		linkToDir: settings?.linkToDir ?? '',
		fillSourceUrls: 'true',
	};
	return fields;
}

export async function uploadFile(
	file: File,
	name: string,
	description: string,
	isPrivate: boolean,
	isInvisible: boolean,
	uploadedFrom: MediabankUploadLocation,
	settings?: MediabankSettings,
	nameTranslations?: GenericTranslation[],
	descriptionTranslations?: GenericTranslation[]
) {
	const fields = formatUploadFileFields(
		name,
		description,
		isPrivate,
		isInvisible,
		uploadedFrom,
		settings,
		nameTranslations,
		descriptionTranslations
	);
	const path = `${apiPath}UploadFile`;
	return upload<MediabankFile>(path, file, fields);
}

export function getThumbPath(path: string) {
	if (!path) return '';

	const fullPath = getPathWithRootPath(path);
	if (fullPath.toLowerCase().indexOf('getfilehandler.ashx') >= 0) {
		const sizeSuffix = fullPath.toLowerCase().includes('thumb=true') ? '' : '&size=sm';
		return `${fullPath}${sizeSuffix}`;
	} else {
		return fullPath;
	}
}

export async function linkExternalFile(
	link: string,
	fileType: number,
	thumbnail: string,
	uploadedFrom: MediabankUploadLocation,
	isPrivate: boolean,
	isInvisible: boolean,
	name: string,
	description: string,
	settings: MediabankFileSettings,
	urlRewrite: string,
	uploadSettings?: MediabankSettings
) {
	const path = `${apiPath}LinkFile`;
	const payload = {
		Link: link,
		Type: fileType,
		Thumbnail: thumbnail,
		UploadedFrom: uploadedFrom.toString(),
		IsPrivate: isPrivate,
		IsInvisible: isInvisible,
		Name: name,
		Description: description,
		Settings: settings,
		UrlRewrite: urlRewrite,
		LinkedObjectId: uploadSettings?.linkToObject?.ObjectId ?? '',
		LinkedObjectType: uploadSettings?.linkToObject?.ObjectType ?? '',
		LinkToDir: uploadSettings?.linkToDir ?? '',
	};

	return await post<MediabankFile>(path, payload);
}

export async function getFrontEndObjectLinks(urlFileFilter: string) {
	const path = `${apiPath}GetFrontEndObjectLinksAsync?urlFilter=${urlFileFilter}`;
	return await get<LinkedMediabankFile[]>(path);
}

export const getBackgroundImageStyle = (mbf: MbfObject | null, fallbackImage?: string | null) => {
	if (mbf && mbf.ImageUrl) {
		return {
			backgroundImage: `url(${getPathWithRootPath(mbf.ImageUrl)})`,
			backgroundPositionX: mbf.Settings?.FocusX > 0 ? `${mbf.Settings.FocusX}%` : 'center',
			backgroundPositionY: mbf.Settings?.FocusY && mbf.Settings?.FocusY > 0 ? `${mbf.Settings.FocusY}%` : 'center',
		};
	}
	if (fallbackImage) {
		return {
			backgroundImage: `url(${getPathWithRootPath(fallbackImage)})`,
			backgroundPosition: 'center',
		};
	}
	return {};
};
